import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack, IoCheckmarkCircle } from 'react-icons/io5';
import { findPassword } from '../../services/authService';

const formatPhone = (val) => {
  const d = val.replace(/[^0-9]/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) return setError('이메일을 입력해 주세요.');
    if (!name.trim()) return setError('이름을 입력해 주세요.');
    if (!phone || phone.replace(/-/g, '').length < 10) return setError('휴대폰 번호를 정확히 입력해 주세요.');

    setLoading(true);
    try {
      await findPassword({ email, name, phone });
      setSuccess(true);
    } catch (err) {
      setError(err.message || '입력하신 정보가 일치하지 않습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Header>
        <HeaderInner>
          <BackBtn type="button" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <IoChevronBack size={22} />
          </BackBtn>
          <HeaderTitle>비밀번호 찾기</HeaderTitle>
        </HeaderInner>
      </Header>

      <Content>
        <Description>
          아래 항목이 모두 일치할 경우 임시 비밀번호를<br />
          문자로 보내드립니다.<br /><br />
          비밀번호 찾기에 어려움이 있으신경우<br />
          고객센터 <Phone>1833-9413</Phone>으로 문의해 주세요.
        </Description>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label>이메일</Label>
            <Input
              type="email"
              placeholder="이메일"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={success}
            />
          </Field>

          <Field>
            <Label>이름</Label>
            <Input
              type="text"
              placeholder="이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              disabled={success}
            />
          </Field>

          <Field>
            <Label>휴대폰 번호</Label>
            <Input
              type="tel"
              placeholder="휴대폰 번호"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              autoComplete="tel"
              disabled={success}
            />
          </Field>

          <SubmitBtn type="submit" disabled={loading || success}>
            {loading ? '발송 중...' : '임시 비밀번호 발송'}
          </SubmitBtn>
        </form>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        {success && (
          <SuccessBox>
            <IoCheckmarkCircle size={20} />
            <div>
              임시 비밀번호가 발송되었습니다.<br />
              문자를 확인 후 로그인해 주세요.
            </div>
          </SuccessBox>
        )}
      </Content>
    </Page>
  );
};

export default ForgotPasswordPage;

// ───── 스타일 ─────
const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgWhite};
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    background: #2f3542;
    align-items: center;
    justify-content: center;
    padding: 40px;
  }
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  z-index: 10;

  @media (min-width: 1024px) {
    position: static;
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
    background: #fff;
    border: 0;
    border-radius: 16px 16px 0 0;
  }
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};

  @media (min-width: 1024px) {
    max-width: 980px;
    height: 64px;
    padding: 0 40px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const HeaderTitle = styled.h1`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0 0 0 ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.realtor.pagePadding} 60px;

  @media (min-width: 1024px) {
    flex: none;
    max-width: 980px;
    background: #fff;
    border-radius: 0 0 16px 16px;
    padding: 60px 40px 80px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);

    > form, > p {
      max-width: 460px;
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin: 0 0 ${({ theme }) => theme.spacing.xxl};
`;

const Phone = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  background: ${({ theme }) => theme.colors.bgWhite};

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.base};
`;

const SuccessBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.base};
  padding: 14px;
  background: #ECFDF5;
  border: 1px solid #A7F3D0;
  border-radius: ${({ theme }) => theme.radius.md};
  color: #065F46;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.5;

  > svg { flex-shrink: 0; }
`;
